import { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Check, MoreHorizontal, Reply, Trash2, User } from 'lucide-react';
import type { Comment } from '@/types';
import { useAuthStore } from '@/stores';
import { formatRelativeTime } from '@/lib/utils';

interface InlineCommentProps {
  comment: Comment;
  onReply: (parentId: number, content: string) => Promise<void>;
  onResolve: (commentId: number, resolved: boolean) => Promise<void>;
  onDelete: (commentId: number) => Promise<void>;
  highlightText?: boolean;
}

export function InlineComment({
  comment,
  onReply,
  onResolve,
  onDelete,
  highlightText = true,
}: InlineCommentProps) {
  const { user } = useAuthStore();
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isReplying && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [isReplying]);

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    setIsSubmitting(true);
    try {
      await onReply(comment.id, replyContent.trim());
      setReplyContent('');
      setIsReplying(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (confirm('确定要删除这条评论吗？')) {
      await onDelete(comment.id);
    }
    setIsMenuOpen(false);
  };

  const handleResolve = async () => {
    await onResolve(comment.id, !comment.resolved);
    setIsMenuOpen(false);
  };

  const canModify = user && (user.id === comment.user_id || user.role === 'admin');

  return (
    <div
      className={`border rounded-lg p-4 ${
        comment.resolved
          ? 'bg-muted/30 border-muted'
          : highlightText
          ? 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800'
          : 'bg-card border-border'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          {comment.user?.avatar_url ? (
            <img
              src={comment.user.avatar_url}
              alt={comment.user.username}
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
              <User className="h-3 w-3" />
            </div>
          )}
          <span className="font-medium text-sm">{comment.user?.username}</span>
          <span className="text-xs text-muted-foreground">
            {formatRelativeTime(comment.created_at)}
          </span>
          {comment.resolved && (
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">
              已解决
            </span>
          )}
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-1 hover:bg-muted rounded"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {isMenuOpen && (
            <div className="absolute right-0 top-8 bg-card border rounded-lg shadow-lg py-1 z-10 min-w-32">
              {canModify && (
                <button
                  onClick={handleDelete}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center space-x-2 text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>删除</span>
                </button>
              )}
              <button
                onClick={handleResolve}
                className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center space-x-2"
              >
                <Check className="h-4 w-4" />
                <span>{comment.resolved ? '取消解决' : '标记已解决'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Selected text (if inline comment) */}
      {comment.selected_text && (
        <div className="mb-2 px-3 py-2 bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-400 text-sm italic rounded-r">
          "{comment.selected_text}"
        </div>
      )}

      {/* Content */}
      <div className="text-sm whitespace-pre-wrap mb-2">{comment.content}</div>

      {/* Actions */}
      {!comment.resolved && (
        <div className="flex items-center space-x-2 mt-3">
          <button
            onClick={() => setIsReplying(!isReplying)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center space-x-1"
          >
            <Reply className="h-3 w-3" />
            <span>回复</span>
          </button>
        </div>
      )}

      {/* Reply input */}
      {isReplying && (
        <div className="mt-3 space-y-2">
          <textarea
            ref={replyInputRef}
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="输入回复..."
            className="w-full px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            rows={2}
          />
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => {
                setIsReplying(false);
                setReplyContent('');
              }}
              className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
            >
              取消
            </button>
            <button
              onClick={handleReply}
              disabled={isSubmitting || !replyContent.trim()}
              className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting ? '发送中...' : '发送'}
            </button>
          </div>
        </div>
      )}

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-3 space-y-3 pl-4 border-l-2 border-muted">
          {comment.replies.map((reply) => (
            <InlineComment
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onResolve={onResolve}
              onDelete={onDelete}
              highlightText={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface InlineCommentPanelProps {
  comments: Comment[];
  onReply: (parentId: number, content: string) => Promise<void>;
  onResolve: (commentId: number, resolved: boolean) => Promise<void>;
  onDelete: (commentId: number) => Promise<void>;
  onClose: () => void;
}

export function InlineCommentPanel({
  comments,
  onReply,
  onResolve,
  onDelete,
  onClose,
}: InlineCommentPanelProps) {
  const unresolvedComments = comments.filter((c) => !c.resolved);
  const resolvedComments = comments.filter((c) => c.resolved);

  return (
    <div className="fixed right-4 top-20 w-96 max-h-[calc(100vh-6rem)] bg-card border rounded-lg shadow-xl overflow-hidden z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-5 w-5" />
          <span className="font-medium">评论 ({comments.length})</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-muted rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      <div className="overflow-y-auto max-h-[calc(100vh-10rem)] p-4 space-y-4">
        {comments.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>暂无评论</p>
            <p className="text-sm">选择文本后点击添加评论</p>
          </div>
        ) : (
          <>
            {unresolvedComments.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">
                  未解决 ({unresolvedComments.length})
                </h4>
                {unresolvedComments.map((comment) => (
                  <InlineComment
                    key={comment.id}
                    comment={comment}
                    onReply={onReply}
                    onResolve={onResolve}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            )}

            {resolvedComments.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">
                  已解决 ({resolvedComments.length})
                </h4>
                {resolvedComments.map((comment) => (
                  <InlineComment
                    key={comment.id}
                    comment={comment}
                    onReply={onReply}
                    onResolve={onResolve}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
